// @ts-nocheck

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Users, TrendingUp, BarChart3, Copy, QrCode, Loader2, Database, Tag, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAffiliateStats } from '@/hooks/useAffiliateStats';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { createDefaultReferralLink } from '@/utils/affiliateUtils';
import { useNavigate } from 'react-router-dom';
import FraudScoreDisplay from '@/components/affiliate/FraudScoreDisplay';
import { useAffiliateCoupons } from '@/hooks/useAffiliateCoupons';

const AffiliateOverviewTab = () => {
  const { profile } = useAuth();
  const { stats, loading: statsLoading, error } = useAffiliateStats();

  const [affiliateCode, setAffiliateCode] = React.useState<string>('');
  const [loadingAffiliateCode, setLoadingAffiliateCode] = React.useState(true);
  const [affiliateId, setAffiliateId] = React.useState<string>('');
  const { coupons, loading: couponsLoading } = useAffiliateCoupons(affiliateId);
  const navigate = useNavigate();

  // Fetch affiliate code on component mount
  React.useEffect(() => {
    const fetchAffiliateCode = async () => {
      if (!profile?.id) return;
      setLoadingAffiliateCode(true);
      try {
        // Get affiliate record (id + current referral_code)
        const { data: affiliate, error: affiliateError } = await supabase
          .from('affiliates')
          .select('id, referral_code')
          .eq('user_id', profile.id)
          .maybeSingle();

        if (affiliateError) {
          console.error('Error fetching affiliate code:', affiliateError);
          return;
        }

        // If affiliate has a code already, use it
        if (affiliate?.referral_code) {
          setAffiliateCode(affiliate.referral_code);
          setAffiliateId(affiliate.id);
          return;
        }

        if (!affiliate?.id) {
          console.warn('Affiliate record not found for user');
          return;
        }

        setAffiliateId(affiliate.id);

        // Try to find any existing referral link to derive code
        const { data: links, error: linksError } = await supabase
          .from('affiliate_referral_links')
          .select('code')
          .eq('affiliate_id', affiliate.id)
          .order('created_at', { ascending: true });

        if (linksError) {
          console.warn('Error fetching referral links:', linksError);
        }

        let codeFromLink = links?.find(l => !!l.code)?.code as string | undefined;

        // If none found, create a default one now
        if (!codeFromLink) {
          try {
            const created = await createDefaultReferralLink(affiliate.id, (profile as any)?.username);
            codeFromLink = created?.code as string | undefined;
          } catch (e) {
            console.warn('Auto-create default referral link failed, falling back to direct code:', e);
            // Fallback: generate and save code directly on affiliates table
            const base = ((profile as any)?.username || profile.id).toString().replace(/[^A-Za-z0-9]/g, '');
            codeFromLink = (`${base}`.slice(0, 20) + 'REF').toUpperCase();
          }
        }

        if (codeFromLink) {
          setAffiliateCode(codeFromLink);
          // Best-effort update of affiliates.referral_code (ignore errors)
          try {
            await supabase.from('affiliates').update({ referral_code: codeFromLink }).eq('id', affiliate.id);
          } catch {}
        }
      } catch (error) {
        console.error('Error in fetchAffiliateCode:', error);
      } finally {
        setLoadingAffiliateCode(false);
      }
    };
    
    fetchAffiliateCode();
  }, [profile?.id]);

  // Generate dynamic affiliate links
  const generateAffiliateUrl = (path: string) => {
    return `https://recruitlook.com${path}?ref=${affiliateCode}`;
  };

  // Predefined quick links
  const quickLinks = [
    { name: 'Sign In', path: '/login' },
    { name: 'Press Page', path: '/press' },
    { name: 'Events Page', path: '/events' }
  ];

  const handleCopyLink = (url: string, name: string) => {
    navigator.clipboard.writeText(url);
    toast.success(`${name} copied to clipboard!`);
  };

  // Calculate conversion rate with proper handling of division by zero
  const conversionRate = stats.total_clicks > 0 ? 
    ((stats.total_conversions / stats.total_clicks) * 100).toFixed(1) : 
    '0.0';

  // Show error state if there's an error fetching stats
  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Stats</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Debug info removed */}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm">Loading...</span>
              </div>
            ) : (
              <>
                 <div className="text-2xl font-bold">${stats.total_earnings.toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.total_earnings > 0 ? 'Great work!' : 'Start earning with referrals'}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.total_clicks}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.total_clicks > 0 ? 'People clicking your links' : 'No clicks yet'}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{conversionRate}%</div>
                <p className="text-xs text-muted-foreground">
                  {stats.total_conversions > 0 ? 'Conversions from clicks' : 'No conversions yet'}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Payouts</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">${(stats.pending_payouts || 0).toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.pending_payouts > 0 ? 'Ready for payout' : 'No pending payouts'}
                </p>
              </>
            )}
          </CardContent>
          </Card>
          
          <FraudScoreDisplay />
        </div>

      {/* Quick Referral Links Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-primary" />
            Your Referral Links
          </CardTitle>
          <CardDescription>Copy and share these links to earn commissions</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingAffiliateCode ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading referral links...</span>
            </div>
          ) : !affiliateCode ? (
            <div className="text-center py-8">
              <Copy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Referral Code Available</h3>
              <p className="text-gray-600">Your referral code is not set up yet. Please contact support.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {quickLinks.map((link) => {
                const fullUrl = generateAffiliateUrl(link.path);
                return (
                  <div key={link.name} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">{link.name}</h4>
                      <div className="text-xs text-gray-500">
                        Current Link
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={fullUrl}
                        readOnly
                        className="flex-1 bg-gray-50 text-sm"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyLink(fullUrl, link.name)}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Coupon Codes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" />
            Your Coupon Codes
          </CardTitle>
          <CardDescription>Share these coupon codes for membership discounts</CardDescription>
        </CardHeader>
        <CardContent>
          {couponsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading coupons...</span>
            </div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-8">
              <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Coupon Codes Yet</h3>
              <p className="text-gray-600 mb-4">Create coupon codes to offer membership discounts</p>
              <Button onClick={() => navigate('/affiliate-dashboard/coupon-codes')}>
                <Tag className="h-4 w-4 mr-2" />
                Create Coupon Codes
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {coupons.slice(0, 3).map((coupon) => (
                <div key={coupon.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono">
                        {coupon.code}
                      </Badge>
                      <span className="text-sm text-gray-600">
                        {coupon.discount_value}% off first month
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(coupon.code);
                        toast.success('Coupon code copied!');
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-green-600 flex items-center gap-1">
                      <span>✓</span>
                      <span>20% off first month of membership</span>
                    </div>
                    <div className="text-xs text-green-600 flex items-center gap-1">
                      <span>✓</span>
                      <span>Your referral link still earns commission on events</span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    <span>Used {coupon.usage_count} times</span>
                    {coupon.usage_limit && <span>/ {coupon.usage_limit} max</span>}
                  </div>
                </div>
              ))}
              
              {coupons.length > 3 && (
                <div className="text-center">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/affiliate-dashboard/coupon-codes')}
                  >
                    View All Coupons ({coupons.length})
                  </Button>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div className="text-xs text-blue-700">
                    <div className="font-medium mb-1">Coupon Restrictions:</div>
                    <ul className="space-y-1">
                      <li>• Coupons only work for membership purchases (20% off first month)</li>
                      <li>• For events and other purchases, share your referral link instead</li>
                      <li>• You still earn commission on all purchases through your referral link</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Manage your affiliate account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg text-center">
              <h3 className="font-medium mb-2">View Analytics</h3>
              <p className="text-sm text-gray-600 mb-3">Track your performance</p>
              <Button variant="outline" size="sm" onClick={() => navigate('/affiliate-dashboard/statistics')}>
                View Stats
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg text-center">
              <h3 className="font-medium mb-2">Coupon Codes</h3>
              <p className="text-sm text-gray-600 mb-3">Create membership discounts</p>
              <Button variant="outline" size="sm" onClick={() => navigate('/affiliate-dashboard/coupon-codes')}>
                <Tag className="h-3 w-3 mr-1" />
                Manage Coupons
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg text-center">
              <h3 className="font-medium mb-2">Request Payout</h3>
              <p className="text-sm text-gray-600 mb-3">Get your earnings</p>
              <Button 
                variant="outline" 
                size="sm"
                disabled={stats.pending_payouts === 0}
                onClick={() => navigate('/affiliate-dashboard/payouts')}
              >
                Request
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateOverviewTab;
